import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import exchangeService from '../../services/exchangeService';
import { getLanguageLabel } from './exchangeConstants';
import ExchangeNav from './ExchangeNav';
import ExchangeProfilePhoto from './ExchangeProfilePhoto';
import { FaSpinner, FaPaperPlane, FaCheck } from 'react-icons/fa';
import '../../ExchangeSearch.css';

// 언어교환 상대를 조건 기반으로 찾아서 리스트로 보여주는 화면이다.
// 스와이프 방식이 아니라, 그냥 카드 목록을 위에서 아래로 훑어보는 방식이다.
// 백엔드가 이미 "내가 배우고 싶은 언어 = 상대 모국어"인 사람을 우선순위로 정렬해서 내려주기 때문에,
// 프론트에서는 정렬을 다시 할 필요가 없고 그냥 순서대로 보여주기만 하면 된다.
const ExchangeSearch = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [needsProfile, setNeedsProfile] = useState(false);
  // 요청을 보낸 상대의 userId를 모아두는 목록이다.
  // 화면 새로고침 없이 "요청 완료" 표시를 바로 보여주기 위해 쓴다.
  const [sentUserIds, setSentUserIds] = useState([]);
  const [sendingUserId, setSendingUserId] = useState(null);

  useEffect(() => {
    fetchCandidates();
  }, []);

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      setNeedsProfile(false);
      const response = await exchangeService.getCandidates();
      setCandidates(response.data || []);
    } catch (err) {
      console.error('후보 조회 오류:', err);
      const serverMessage = err.response && err.response.data && err.response.data.message;
      if (serverMessage && serverMessage.includes('프로필을 등록')) {
        // 프로필을 먼저 등록해야 하는 상황이다. 이 경우는 에러라기보다는 안내 화면을 보여줘야 한다.
        setNeedsProfile(true);
      } else {
        setErrorMessage(serverMessage || '후보 목록을 불러오는 중 오류가 발생했다.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendRequest = async (userId) => {
    // 게스트은 후보 목록은 볼 수 있지만, 매칭 요청은 로그인해야 보낼 수 있다.
    if (!user) {
      alert('매칭 요청은 로그인 후 이용할 수 있다.');
      navigate('/login');
      return;
    }
    try {
      setSendingUserId(userId);
      await exchangeService.sendMatchRequest(userId);
      setSentUserIds((prev) => [...prev, userId]);
    } catch (err) {
      console.error('매칭 요청 오류:', err);
      const serverMessage = err.response && err.response.data && err.response.data.message;
      alert(serverMessage || '매칭 요청 중 오류가 발생했다.');
    } finally {
      setSendingUserId(null);
    }
  };

  if (loading) {
    return (
      <>
        <ExchangeNav />
        <div className="exchange-loading">
          <FaSpinner className="exchange-spinner" />
          <p>불러오는 중이다...</p>
        </div>
      </>
    );
  }

  if (needsProfile) {
    return (
      <>
        <ExchangeNav />
        <div className="exchange-search-container">
          <div className="exchange-empty-state">
            <p>언어교환 상대를 찾으려면 먼저 프로필을 등록해야 한다.</p>
            <Link to="/exchange/profile" className="exchange-empty-action-button">
              프로필 등록하러 가기
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <ExchangeNav />
      <div className="exchange-search-container">
        <h1 className="exchange-search-title">언어교환 상대 찾기</h1>
        <p className="exchange-search-subtitle">
          내가 배우고 싶은 언어가 모국어이고, 내 모국어를 배우고 싶어하는 사람이 위쪽에 먼저 보여진다.
        </p>

        {errorMessage && <div className="exchange-error">{errorMessage}</div>}

        {candidates.length === 0 ? (
          <div className="exchange-empty-state">
            <p>지금은 조건에 맞는 상대가 없다. 나중에 다시 확인해달라.</p>
          </div>
        ) : (
          <div className="exchange-candidate-list">
            {candidates.map((candidate) => {
              const alreadySent = sentUserIds.includes(candidate.userId);
              const isSending = sendingUserId === candidate.userId;
              return (
                <div key={candidate.userId} className="exchange-candidate-card">
                  <div className="exchange-candidate-header">
                    <ExchangeProfilePhoto photoUrl={candidate.photoUrl} size={48} alt={`${candidate.nickname} 프로필 사진`} />
                    <div className="exchange-candidate-header-text">
                      <span className="exchange-candidate-nickname">{candidate.nickname}</span>
                      <span className="exchange-candidate-age">
                        {candidate.age != null ? `만 ${candidate.age}세` : ''}
                        {candidate.gender ? ` · ${candidate.gender}` : ''}
                      </span>
                    </div>
                  </div>

                  {candidate.bio && <p className="exchange-candidate-bio">{candidate.bio}</p>}

                  <div className="exchange-candidate-languages">
                    <div className="exchange-language-row">
                      <span className="exchange-language-row-label">도와줄 수 있는 언어</span>
                      <span className="exchange-language-tags">
                        {candidate.nativeLanguages.map((code) => (
                          <span key={code} className="exchange-language-tag native">
                            {getLanguageLabel(code)}
                          </span>
                        ))}
                      </span>
                    </div>
                    <div className="exchange-language-row">
                      <span className="exchange-language-row-label">배우고 싶은 언어</span>
                      <span className="exchange-language-tags">
                        {candidate.learningLanguages.map((code) => (
                          <span key={code} className="exchange-language-tag learning">
                            {getLanguageLabel(code)}
                          </span>
                        ))}
                      </span>
                    </div>
                  </div>

                  <button
                    className="exchange-request-button"
                    onClick={() => handleSendRequest(candidate.userId)}
                    disabled={alreadySent || isSending}
                  >
                    {alreadySent ? (
                      <>
                        <FaCheck /> 요청 완료
                      </>
                    ) : isSending ? (
                      '보내는 중이다...'
                    ) : (
                      <>
                        <FaPaperPlane /> 매칭 요청
                      </>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default ExchangeSearch;
